import { create } from 'zustand';
import { createFileLogger } from '../utils/logger';
import { spaceCRDTService } from '../services/crdt/spaceCRDTService';
import useSpaceStore from './spaceStore';

const log = createFileLogger('SPATIAL_UI_STORE');

declare global {
  interface Window {
    __windowDragging?: boolean;
    __lastDragUpdate?: number;
    __lastDragWindowId?: string;
    __lastDragPosition?: { x: number; y: number };
  }
}

export interface WindowState {
  position?: { x: number; y: number };
  size?: { width: number; height: number };
}

const windowDragState = {
  isDragging: false,
  pendingSave: false,
  lastDragTime: 0
};

interface SpatialUIStore {
  windowStates: Record<string, WindowState>;
  windowZIndexes: Record<string, number>;
  
  updateWindowPosition: (windowId: string, position: { x: number; y: number }) => void;
  updateWindowSize: (windowId: string, size: { width: number; height: number }) => void;
  setWindowState: (windowId: string, state: WindowState) => void;
  bringWindowToFront: (windowId: string) => void;
  finalizeWindowDrag: (
    windowId: string,
    position: { x: number; y: number },
    spaceId: string,
    onSave: () => Promise<void>
  ) => void;
  
  /** Restore persisted z-order from CRDT window objects after space load. */
  hydrateZIndexesFromWindows: (windows: Record<string, { zIndex?: number }>) => void;

  reset: () => void;
}

export const useSpatialUIStore = create<SpatialUIStore>((set, get) => ({
  windowStates: {},
  windowZIndexes: {},
  
  updateWindowPosition: (windowId: string, position: { x: number; y: number }) => {
    set(state => ({
      windowStates: {
        ...state.windowStates,
        [windowId]: {
          ...state.windowStates[windowId],
          position
        }
      }
    }));
  },
  
  updateWindowSize: (windowId: string, size: { width: number; height: number }) => {
    set(state => ({
      windowStates: {
        ...state.windowStates,
        [windowId]: {
          ...state.windowStates[windowId],
          size
        }
      }
    }));
  },
  
  setWindowState: (windowId: string, state: WindowState) => {
    set(store => ({
      windowStates: {
        ...store.windowStates,
        [windowId]: state
      }
    }));
  },
  
  bringWindowToFront: (windowId: string) => {
    const { windowZIndexes } = get();
    
    const maxZIndex = Math.max(0, ...Object.values(windowZIndexes));
    const newZ = maxZIndex + 1;
    const newZIndexes = {
      ...windowZIndexes,
      [windowId]: newZ
    };
    
    const electron = (window as any).electron;
    if (electron?.BrowserView?.reorder) {
      electron.BrowserView.reorder(newZIndexes);
    }
    
    set({ windowZIndexes: newZIndexes });

    // Persist zIndex so stacking order survives reload
    const spaceId = useSpaceStore.getState().activeSpace?.id;
    if (spaceId) {
      spaceCRDTService.updateWindow(spaceId, windowId, { zIndex: newZ } as any);
    }
  },
  
  finalizeWindowDrag: (
    windowId: string,
    position: { x: number; y: number },
    spaceId: string,
    onSave: () => Promise<void>
  ) => {
    windowDragState.isDragging = false;
    
    if (!spaceId) return;
    
    get().updateWindowPosition(windowId, position);

    // Include the current zIndex in the final position commit so the CRDT
    // snapshot always has the latest stacking order.
    const currentZ = get().windowZIndexes[windowId];
    const updates: Record<string, any> = { position };
    if (typeof currentZ === 'number') updates.zIndex = currentZ;
    spaceCRDTService.updateWindow(spaceId, windowId, updates as any);
    
    if ((window as any).__lastDragWindowId === windowId) {
      delete (window as any).__lastDragWindowId;
      delete (window as any).__lastDragPosition;
    }
    
    windowDragState.pendingSave = false;
    
    onSave().catch(error => {
      log.error('[SpatialUIStore] Error saving window positions after drag:', error);
    });
  },
  
  /** Called on space init to restore persisted z-order from CRDT window objects. */
  hydrateZIndexesFromWindows: (windows: Record<string, { zIndex?: number }>) => {
    const restored: Record<string, number> = {};
    for (const [id, w] of Object.entries(windows)) {
      if (typeof w.zIndex === 'number') restored[id] = w.zIndex;
    }
    if (Object.keys(restored).length > 0) {
      set({ windowZIndexes: restored });
      log.info('[SpatialUIStore] Restored zIndexes from CRDT:', Object.keys(restored).length, 'windows');
    }
  },

  reset: () => {
    set({
      windowStates: {},
      windowZIndexes: {}
    });
  }
}));

export default useSpatialUIStore;


